/* eslint-disable import/prefer-default-export */
import { takeEvery, takeLatest, fork, put } from 'redux-saga/effects';

import {
  createFetchDataActionTypes,
  createFetchDataActions,
  createFetchDataReduceFunctions,
  createFetchDataSaga,
  createFetchDataSelectors,
  createInitWithDefaultData,
} from './entity-descriptor-data';

import {
  createDeleteActionTypes,
  createDeleteActions,
  createDeleteSaga,
  createDeleteReduceFunctions,
  createDeleteSelectors,
} from './entity-descriptor-delete';

import {
  createFetchListSaga,
  createFetchListActionTypes,
  createFetchListActions,
  createFetchListReduceFunctions,
  createFetchListSelectors,
} from './entity-descriptor-list';

import {
  createUpdateActionTypes,
  createUpdateActions,
  createUpdateReduceFunctions,
  createUpdateSelectors,
  createUpdateSaga,
} from './entity-descriptor-update';

import {
  createCreateActionTypes,
  createCreateReduceFunctions,
  createCreateActions,
  createCreateSaga,
  createCreateSelectors,
} from './entity-descriptor-create';

import { getLogger } from '../logger/index';

if (!global.reduxEntities) {
  global.reduxEntities = {};
}

const INITIAL_ENTITY_STATE = {
  defaultData: {},
  data: null,
  count: 0,
  list: [],
  dataErrors: [],
  listErrors: [],
  deleteErrors: [],
  updateErrors: [],
  createErrors: [],
  dataLoading: false,
  listLoading: false,
  deleteLoading: false,
  updateLoading: false,
  createLoading: false,
};

function createActions(baseName) {
  const actionPrefix = baseName.toUpperCase();
  const fetchDataActionTypes = createFetchDataActionTypes(actionPrefix);
  const fetchListActionTypes = createFetchListActionTypes(actionPrefix);
  const deleteActionTypes = createDeleteActionTypes(actionPrefix);
  const updateActionTypes = createUpdateActionTypes(actionPrefix);
  const createActionTypes = createCreateActionTypes(actionPrefix);

  const result = {
    name: baseName,
    actionTypes: {
      ...fetchDataActionTypes,
      ...fetchListActionTypes,
      ...deleteActionTypes,
      ...updateActionTypes,
      ...createActionTypes,
    },
    ...createFetchListActions(fetchListActionTypes),
    ...createFetchDataActions(fetchDataActionTypes),
    ...createDeleteActions(deleteActionTypes),
    ...createUpdateActions(updateActionTypes),
    ...createCreateActions(createActionTypes),
  };
  return result;
}

function createEntityReducer(entityDescriptor, options) {
  const initialState = { ...INITIAL_ENTITY_STATE };
  const reduceFunctions = {
    ...createFetchListReduceFunctions(entityDescriptor, options),
    ...createFetchDataReduceFunctions(entityDescriptor, options),
    ...createDeleteReduceFunctions(entityDescriptor, options),
    ...createUpdateReduceFunctions(entityDescriptor, options),
    ...createCreateReduceFunctions(entityDescriptor, options),
  };

  return (state = initialState, action) => {
    const reducer = reduceFunctions[action.type];
    if (reducer) {
      return reducer(state, action.payload);
    }
    return state;
  };
}

function createEntitySaga(entityDescriptor, options) {
  let lastListAction;
  let lastDataAction;

  return function* entitySaga() {
    if (options.data) {
      yield takeEvery(entityDescriptor.actionTypes.FETCH_DATA, function* fetchData(action) {
        yield fork(createFetchDataSaga(options.data.url, entityDescriptor, options.data), action);
        lastDataAction = action;
      });

      yield takeEvery(entityDescriptor.actionTypes.GET_OR_FETCH_DATA, function* getOrfetchData(action) {
        yield fork(createFetchDataSaga(options.data.url, entityDescriptor, options.data, true), action);
        lastDataAction = action;
      });
      yield takeEvery(
        entityDescriptor.actionTypes.INIT_WITH_DEFAULT_DATA,
        createInitWithDefaultData(entityDescriptor, options.data)
      );
      yield takeLatest('DATA_RESET', function* dataReset() {
        if (lastDataAction) {
          yield put(lastDataAction);
        }
      });
    }
    if (options.list) {
      yield takeLatest(entityDescriptor.actionTypes.FETCH_LIST, function* fetchList(action) {
        yield fork(createFetchListSaga(options.list.url, entityDescriptor, options.list), action);
        lastListAction = action;
      });
      yield takeLatest('LIST_RESET', function* listReset() {
        if (lastListAction) {
          yield put({
            ...lastListAction,
            payload: {
              ...lastListAction.payload,
              fetchNextChunk: false,
            },
          });
        }
      });
    }

    if (options.delete) {
      yield takeEvery(
        entityDescriptor.actionTypes.DELETE,
        createDeleteSaga(options.delete.url, entityDescriptor, options.delete)
      );
    }

    if (options.update) {
      yield takeEvery(
        entityDescriptor.actionTypes.UPDATE,
        createUpdateSaga(options.update.url, entityDescriptor, options.update)
      );
    }

    if (options.create) {
      yield takeEvery(
        entityDescriptor.actionTypes.CREATE,
        createCreateSaga(options.create.url, entityDescriptor, options.create)
      );
    }
  };
}

export function createSelectors(moduleName, entity) {
  return {
    ...createFetchListSelectors(moduleName, entity),
    ...createFetchDataSelectors(moduleName, entity),
    ...createDeleteSelectors(moduleName, entity),
    ...createUpdateSelectors(moduleName, entity),
    ...createCreateSelectors(moduleName, entity),
  };
}

export function createEntityDescriptor(entityName, options) {
  let entityDescriptor = global.reduxEntities[entityName];
  if (global.isReduxSetup && !entityDescriptor) {
    throw new Error(
      'Redux is already setup. Entities must be registered earlier. Entity "' + entityName + '" is ignored.'
    );
  }
  // eslint-disable-next-line no-param-reassign
  options.primaryKey = options.primaryKey || 'id';
  if (!entityDescriptor) {
    entityDescriptor = createActions(entityName);
    entityDescriptor.primaryKey = options.primaryKey;
    entityDescriptor.logger = getLogger(entityName);
    entityDescriptor.__decorator = options.decorator;
    entityDescriptor.__reducer = createEntityReducer(entityDescriptor, options);
    entityDescriptor.__saga = createEntitySaga(entityDescriptor, options);
    global.reduxEntities[entityName] = entityDescriptor;
  }
  return entityDescriptor;
}

export { waitForList } from './entity-descriptor-list';
